"use client";

import { Analytics } from "@vercel/analytics/next";
import { SpeedInsights } from "@vercel/speed-insights/next";
import { useEffect, useState } from "react";

import { dropEventUnlessAllowed, isTelemetryAllowedInBrowser, subscribeToConsent } from "@/src/features/consent/browserConsent";

/**
 * The single gate for Vercel Analytics and Speed Insights. They mount only
 * while the "analytics" consent category allows it and the account is not in
 * HIPAA mode (`shouldLoadTelemetry` decides both, including the default: on
 * outside the EU/UK, opt-in inside). Nothing renders on the server or before
 * hydration, so no tracker script loads before the browser-side check.
 * PostHog and Axiom are gated in src/analytics/client.ts and
 * instrumentation-client.ts through the same decision.
 *
 * The compliance mode comes from browserConsent: the verified session's mode,
 * else the HIPAA device hint. useAuthStatus applies the verified mode through
 * setBrowserTelemetryComplianceMode before the store gets it, and that setter
 * dispatches the consent-changed event, so this gate never reads the app store
 * (which would pull the whole store into every public page).
 *
 * Vercel's inject() has no teardown, so unmounting alone would leave the
 * scripts sending until reload. `beforeSend` re-checks consent and the
 * account's compliance mode on every event and drops it once either denies.
 */
const beforeSend = dropEventUnlessAllowed("analytics");

export default function ConsentGatedTelemetry({ vercelAnalytics }: { vercelAnalytics: boolean }) {
  const [analyticsAllowed, setAnalyticsAllowed] = useState(false);

  useEffect(() => {
    const apply = () => setAnalyticsAllowed(isTelemetryAllowedInBrowser("analytics"));
    apply();
    return subscribeToConsent(apply);
  }, []);

  if (!vercelAnalytics || !analyticsAllowed) return null;
  return (
    <>
      <SpeedInsights beforeSend={beforeSend} />
      <Analytics beforeSend={beforeSend} />
    </>
  );
}
